import { useCallback } from 'react';
import * as R from 'remeda';
import { useSelector } from 'react-redux';
import { useShallow } from 'zustand/react/shallow';
import { Box, TableCell } from '@rossum/ui/material';
import FooterDatapoint from '../../components/Datapoint/FooterDatapoint';
import { isFieldEditableFormula } from '../../components/Datapoint/helpers';
import { useDocumentStore } from '../../features/annotation-view/document-store/DocumentStore';
import { findDatapointById } from '../../redux/modules/datapoints/navigation/findDatapointIndex';
import { datapointPathSelector } from '../../redux/modules/datapoints/selector';
import { isFieldEditable } from '../../redux/modules/schema/helpers';
import { schemaMapSelector } from '../../redux/modules/schema/schemaMapSelector';
import {
  annotationValidationSelector,
  sidebarAutomationBlockersByDefaultVisibleSelector,
} from '../../redux/modules/user/selectors';
import { AnyDatapointSchema } from '../../types/schema';
import { State } from '../../types/state';
import { BatchUpdateEvent } from './useBatchUpdate';
import {
  calculateWidthStyle,
  commonCellStyle,
  includedInBatchUpdateStyle,
} from './utils';

export const FooterDatapointTableCell = ({
  id,
  tupleId,
  column,
  onBatchUpdateEvent,
  isIncludedInBatchUpdate,
  readOnly,
  isGhostRow,
  widthOverride,
}: {
  id: number;
  tupleId: number;
  column: AnyDatapointSchema;
  onBatchUpdateEvent: (event: BatchUpdateEvent) => void;
  isIncludedInBatchUpdate: boolean;
  readOnly: boolean;
  isGhostRow: boolean;
  widthOverride?: number;
}) => {
  const schemaMap = useSelector(schemaMapSelector);
  const datapoint = useSelector((state: State) => {
    return findDatapointById(state.datapoints.content, id);
  });

  // if datapoint does not exist, state is invalid anyway and we render nothing
  const schema = schemaMap.get(datapoint?.schemaId ?? '');

  const isEditDisabled = !isFieldEditable(schema);

  const isLoading = useSelector((state: State) =>
    state.datapoints.loadingDatapointIds.includes(id)
  );
  const message = useSelector((state: State) => state.datapoints.messages[id]);

  const [isActive, path] = useSelector((state: State) => {
    const [sectionId, multivalueId, _, datapointIdInPath] =
      datapointPathSelector(state);

    const result: [boolean, number[]] = [
      datapointIdInPath === datapoint?.id,
      // if datapoint does not exist, state is invalid and we render nothing anyway
      [sectionId ?? 0, multivalueId ?? 0, tupleId, datapoint?.id ?? 0],
    ];

    return result;
  }, R.isDeepEqual);

  const startDrag = useCallback(() => {
    onBatchUpdateEvent({
      type: 'start',
      tupleId,
      columnSchemaId: column.id,
      isEditableFormula: isFieldEditableFormula(schema),
    });
  }, [onBatchUpdateEvent, tupleId, column.id, schema]);

  const endDrag = useCallback(() => {
    onBatchUpdateEvent({ type: 'end' });
  }, [onBatchUpdateEvent]);

  const automationBlockersDefault = useSelector(
    sidebarAutomationBlockersByDefaultVisibleSelector
  );

  const annotationValidationMode = useSelector(annotationValidationSelector);

  const { blockersVisible } = useDocumentStore(
    useShallow(state => ({
      blockersVisible: state.sidebarState.blockersVisible,
    }))
  );

  if (!datapoint) {
    return null;
  }

  return (
    <TableCell
      key={datapoint.id}
      scope="row"
      sx={{
        padding: 0,
        pl: '1px',
        position: 'relative',
        ...commonCellStyle,
      }}
      style={calculateWidthStyle(column, widthOverride)}
      data-schema-id={column.id}
    >
      {isIncludedInBatchUpdate ? <Box sx={includedInBatchUpdateStyle} /> : null}
      <FooterDatapoint
        active={isActive}
        data={datapoint}
        isLoading={isLoading}
        startDrag={startDrag}
        endDrag={endDrag}
        widthOverride={widthOverride}
        blockersVisible={!!automationBlockersDefault || blockersVisible}
        annotationValidationMode={annotationValidationMode}
        /* AFI TODO */
        hideDragger={
          /* tuples.length <= 1 || */ schema?.type === 'button' ||
          isEditDisabled
        }
        inFooter
        isSimpleMultivalue={false}
        readOnly={readOnly}
        myPath={path}
        message={message}
        schema={schema}
        isGhostRow={isGhostRow}
      />
    </TableCell>
  );
};
